import {
  transactionExportToCSV,
  TransactionsTabView,
  useInterval,
  ApproveMfaDialog,
} from '@valiant/ui-shared'
import { environment } from '../../environment'
import type { FilterStatus, TransactionData } from '@valiant/ui-shared'
import {
  useApproveSwapMutation,
  useGetTransactionsQuery,
  useRejectSwapMutation,
  type TransactionStatus,
  type TransactionType,
} from '../../generated/graphql'
import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { maskSwapStatus } from '../../utils/swapStatus'
import type { SwapStatus } from '@valiant/ui-shared'
import { useDebounce } from '@valiant/ui-shared'
import { useQueryParams } from '@valiant/ui-shared'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../../AuthProvider'

interface TransactionsTabProps {
  initialStatus?: string | null
  groupId?: string
  companyId?: string
  searchTerm?: string
}

export function TransactionsTab({
  initialStatus = null,
  groupId,
  companyId,
  searchTerm,
}: TransactionsTabProps) {
  const { auth } = useAuth()
  const navigate = useNavigate()
  const queryParams = useQueryParams()

  // Initialize values from query params with fallback to props
  const searchInput = searchTerm || queryParams.search || ''
  const selectedStatus = (queryParams.status as TransactionStatus | null) || initialStatus
  const selectedType = queryParams.type as TransactionType | null
  const isFlagged =
    queryParams.isFlagged === 'true' ? true : queryParams.isFlagged === 'false' ? false : null

  const debouncedSearch = useDebounce(searchInput)

  const filter = useMemo(() => {
    interface FilterObject {
      search?: string
      status?: TransactionStatus
      type?: TransactionType
      groupId?: string
      companyId?: string
      isFlagged?: boolean
    }

    const filterObj: FilterObject = {}
    if (debouncedSearch.trim()) filterObj.search = debouncedSearch.trim()
    if (selectedStatus) filterObj.status = selectedStatus as TransactionStatus
    if (selectedType) filterObj.type = selectedType
    if (isFlagged !== null) filterObj.isFlagged = isFlagged
    if (groupId) filterObj.groupId = groupId
    if (companyId) filterObj.companyId = companyId
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, selectedStatus, selectedType, isFlagged, groupId, companyId])

  // Pagination state
  const [pagination, setPagination] = useState<{
    first?: number
    after?: string
    last?: number
    before?: string
  }>({
    first: 20,
  })

  // Reset pagination when filters or search change
  useEffect(() => {
    setPagination({ first: 20 })
  }, [debouncedSearch, selectedStatus, selectedType, isFlagged])

  const [{ data: transactionsData, fetching: transactionsLoading }, refetchTransactions] =
    useGetTransactionsQuery({
      variables: { filter, pagination },
      requestPolicy: 'network-only',
    })

  // Track whether the initial fetch has completed to avoid flickering on background refetches
  const hasLoadedRef = useRef(false)
  if (!transactionsLoading) hasLoadedRef.current = true

  // Auto-refetch every 10 seconds to catch status changes, without showing loading state
  useInterval(() => {
    refetchTransactions({ requestPolicy: 'network-only' })
  }, 10000)

  // State for manual refresh loading
  const [isManualRefreshing, setIsManualRefreshing] = useState(false)

  // MFA dialog state
  const [showMfaDialog, setShowMfaDialog] = useState(false)
  const [selectedSwapId, setSelectedSwapId] = useState<string | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  const [, approveSwap] = useApproveSwapMutation()
  const [, rejectSwap] = useRejectSwapMutation()
  // const [, approveDeposit] = useApproveDepositMutation()

  const transactions: TransactionData[] = (transactionsData?.getTransactions?.nodes || []).map(
    (transaction) => ({
      ...transaction,
      swapStatus: maskSwapStatus(
        transaction.swapStatus,
        transaction.isExternalApi,
      ) as SwapStatus | null | undefined,
    }),
  )

  // Pagination handlers
  const handleNextPage = (cursor: string) => {
    setPagination({ first: 20, after: cursor })
  }

  const handlePreviousPage = (cursor: string) => {
    setPagination({ last: 20, before: cursor })
  }

  const handleApprove = (transactionId: string) => {
    // Find the transaction to determine its type
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) {
      toast.error('Transaction not found')
      return
    }

    if (transaction.type === 'DEPOSIT') {
      toast.error('Deposit rejection is not available to users. Please contact an administrator.')
      return
    } else if (transaction.type === 'TRADE') {
      setSelectedSwapId(transactionId)
      setShowMfaDialog(true)
    } else {
      toast.error('Unknown transaction type')
    }
  }

  const handleApproveSwap = useCallback(
    async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
      if (!selectedSwapId) return

      setIsApproving(true)
      try {
        const result = await approveSwap({ input: { swapId: selectedSwapId, ...proof } })
        if (result.error) {
          throw new Error(result.error.message)
        }
        if (result.data) {
          toast.success('Swap approved successfully')
          setShowMfaDialog(false)
          setSelectedSwapId(null)
        } else {
          toast.error('Failed to approve swap')
          throw new Error('Failed to approve swap')
        }
      } catch (error) {
        console.error('Error approving swap:', error)
        toast.error('Failed to approve swap')
      } finally {
        setIsApproving(false)
      }
    },
    [approveSwap, selectedSwapId],
  )

  const handleRejectTransaction = async (transactionId: string) => {
    // Find the transaction to determine its type
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) {
      toast.error('Transaction not found')
      throw new Error('Transaction not found')
    }

    try {
      if (transaction.type === 'DEPOSIT') {
        toast.error('Deposit rejection is not available to users. Please contact an administrator.')
        return
      } else if (transaction.type === 'TRADE') {
        await rejectSwap({ input: { swapId: transactionId, reason: 'Rejected by admin' } })
        toast.success('Swap rejected successfully')
      } else {
        toast.error('Unknown transaction type')
        throw new Error('Unknown transaction type')
      }
    } catch (error) {
      toast.error('Failed to reject transaction')
      console.error('Error rejecting transaction:', error)
      throw error
    }
  }

  const handleViewDetails = (transactionId: string, type: string) => {
    if (type === 'DEPOSIT') {
      navigate(`/transactions/deposit/${transactionId}`)
    } else if (type === 'TRADE') {
      navigate(`/transactions/swap/${transactionId}`)
    }
  }

  const exportToCSV = () => {
    transactionExportToCSV(transactions, environment.MSB)
  }

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true)
    try {
      await refetchTransactions({ requestPolicy: 'network-only' })
      toast.success('Transactions refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh transactions')
      console.error('Error refreshing transactions:', error)
    } finally {
      setIsManualRefreshing(false)
    }
  }

  // Function to update filters atomically
  const updateFilters = (
    status: FilterStatus | null,
    type: TransactionType | null,
    flagged: boolean | null = null,
  ) => {
    queryParams.updateMultiple({
      status: status || undefined,
      type: type || undefined,
      isFlagged: flagged === null ? undefined : flagged.toString(),
    })
  }

  return (
    <>
      <TransactionsTabView
        transactions={transactions}
        loading={transactionsLoading && !hasLoadedRef.current}
        selectedStatus={selectedStatus}
        selectedType={selectedType}
        isFlagged={isFlagged}
        onFilter={updateFilters}
        onApproveTransaction={handleApprove}
        onRejectTransaction={handleRejectTransaction}
        onViewTransactionDetails={handleViewDetails}
        onExportCSV={exportToCSV}
        onRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
        showExportButton={true}
        showRefreshButton={true}
        showActionButtons={true}
        showFlagColumn={true}
        hideDepositActions={true}
        // Pagination props
        pageInfo={transactionsData?.getTransactions?.pageInfo}
        totalCount={transactionsData?.getTransactions?.totalCount || 0}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        showPagination={true}
      />

      <ApproveMfaDialog
        open={showMfaDialog}
        onOpenChange={setShowMfaDialog}
        onConfirm={handleApproveSwap}
        isSubmitting={isApproving}
        title="Approve Swap"
        apiUrl={environment.API_URL}
        token={auth?.token}
        action="approveSwap"
        targetKey={selectedSwapId ?? undefined}
      />
    </>
  )
}
